﻿import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import {
  createEffect,
  createMemo,
  createSignal,
  lazy,
  on,
  onMount,
  Show,
  Suspense,
} from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import { ProcessingStatusBadge } from "~/components/document/processing-status-badge";
import { WorkflowStepsPanel } from "~/components/document/workflow-steps";
import {
  IconCircleDollarSign,
  IconDownload,
  IconFileText,
  IconInfo,
  IconLink,
  IconLoaderCircle,
  IconPencil,
  IconTextAlignStart,
  IconTrash,
  IconTriangleAlert,
} from "~/components/icons";
import { Markdown } from "~/components/markdown";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { MultiSelect } from "~/components/ui/multi-select";
import { useIsMobile } from "~/components/ui/sidebar";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import { businessesResource } from "~/features/business/business.resource";
import { getDocumentDetailPageData } from "~/features/document/document-detail-page";
import {
  deleteDocument,
  getPresignedDownloadUrl,
} from "~/features/document/document.api";
import { useDocumentForm } from "~/features/document/document.form";
import { DocumentDetailResource } from "~/features/document/document.resource";
import {
  DocumentTypes,
  type DocumentType,
} from "~/features/document/document.schema";
import type { DocumentDetail } from "~/features/document/document.server";
import { createVendor } from "~/features/vendor/vendor.api";
import { VendorsResource } from "~/features/vendor/vendor.resource";
import {
  WorkflowConfigResource,
  WorkflowStepsResource,
} from "~/features/workflow/workflow.resource";
import {
  WORKFLOW_STEP_LABELS,
  type WorkflowStep,
} from "~/features/workflow/workflow.schema";
import { withBusinessGuard } from "~/lib/route-guards";
import { importFromExcel } from "~/lib/spreadsheet-export";
import { formatDisplayCalendarDate, formatDisplayDate } from "~/lib/utils";

const PdfViewer = lazy(() =>
  import("~/components/document/base-pdf-viewer").then((m) => ({
    default: m.PdfViewer,
  })),
);

const Spreadsheet = lazy(() => import("~/components/spreadsheet/spreadsheet"));

export const Route = createFileRoute(
  "/_dashboard/dashboard/documents/$documentId",
)({
  staticData: {
    breadcrumb: (m) => m.loaderData?.document?.name ?? "Document",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a document doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Document not found"
      message="This document doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/documents"
      homeLabel="Go to documents"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    // Kick off the presign in parallel with the page data — it only needs
    // {id, businessId}, not the document payload. The PDF viewer needs the URL,
    // and waiting for the viewer to mount added ~350ms of serial latency, but
    // awaiting it *after* the page data here made the loader run two server
    // calls back-to-back (~2s each on staging), doubling the transition time.
    // Running them concurrently halves the loader's critical path.
    // Non-fatal: the preview component refetches on mount if this fails.
    const presignPromise = getPresignedDownloadUrl({
      data: { id: params.documentId, businessId },
    })
      .then((r) => r.presignedUrl)
      .catch(() => null);

    const data = await getDocumentDetailPageData({
      data: { businessId, documentId: params.documentId },
    });
    DocumentDetailResource.seed(
      context.queryClient,
      { businessId, id: params.documentId },
      data.document,
    );

    // Prefetch the detail page's component-level queries so they resolve from
    // cache on mount instead of flashing loading states. These run during the
    // route preload (hover), so by the time the user clicks, the workflow
    // steps, associations, and association tab lists are already cached.
    // They're kicked off in parallel and NOT awaited — they must not block the
    // loader's critical path (the document header renders from loader data).
    const entityType = "document";
    const entityId = params.documentId;
    void context.queryClient.prefetchQuery(
      WorkflowStepsResource.options({ businessId, entityType, entityId }),
    );
    void context.queryClient.prefetchQuery(
      WorkflowConfigResource.options({ businessId, entityType }),
    );
    prefetchAssociations(context.queryClient, businessId, entityType, entityId);

    const presignedUrl = data.document.fileKey ? await presignPromise : null;

    return { businessId, document: data.document, presignedUrl };
  },
  component: DocumentDetailPage,
});

type Mode = "view" | "edit";

// -- Page ----------------------------------------------------------------------

function DocumentDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  DocumentDetailResource.seedIfEmpty(
    qc,
    { businessId: data().businessId, id: params().documentId },
    data().document,
  );

  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const PROCESSING_STATUSES = new Set([
    "queued",
    "ocr_running",
    "ocr_complete",
    "extract_running",
  ]);

  const documentQuery = createQuery(() => ({
    ...DocumentDetailResource.options({
      businessId: data().businessId,
      id: params().documentId,
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const status = query.state.data?.processingStatus;
      return status && PROCESSING_STATUSES.has(status) ? 3000 : false;
    },
  }));

  // When processing finishes, invalidate the document list cache so the table
  // shows fresh data immediately on navigation back (instead of stale 30s data).
  createEffect(
    on(
      () => documentQuery.data?.processingStatus,
      (status, prev) => {
        if (status === "complete" && prev && PROCESSING_STATUSES.has(prev)) {
          qc.invalidateQueries({
            queryKey: ["document", data().businessId],
          });
        }
      },
    ),
  );

  return (
    <div class="flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10">
      <Show
        when={documentQuery.data}
        fallback={
          <div role="status" class="w-full animate-pulse">
            <div class="h-150 bg-muted rounded-lg w-full" />
          </div>
        }
      >
        {(doc) => (
          <Show
            when={mode() === "edit"}
            fallback={
              <DocumentView
                document={doc()}
                businessId={data().businessId}
                prefetchedUrl={data().presignedUrl}
                setMode={setMode}
                onDeleted={() => {
                  DocumentDetailResource.remove(qc, {
                    businessId: data().businessId,
                    id: params().documentId,
                  });
                  // Remove the list cache so the table doesn't flash the
                  // deleted row before a background refetch.
                  qc.removeQueries({
                    queryKey: ["document", data().businessId],
                  });
                  navigate({ to: "/dashboard/documents" });
                }}
              />
            }
          >
            <DocumentEdit
              document={doc()}
              businessId={data().businessId}
              onCancel={() => setMode("view")}
              onSaved={() => {
                DocumentDetailResource.invalidate(qc, {
                  businessId: data().businessId,
                  id: params().documentId,
                });
                // The document's vendor may have changed on save, which
                // re-syncs the document↔vendor association server-side.
                // Invalidate the associations cache so the Associations
                // panel (vendor table) reflects the change immediately.
                qc.invalidateQueries({
                  queryKey: [
                    "association",
                    data().businessId,
                    "document",
                    params().documentId,
                  ],
                });
                setMode("view");
              }}
            />
          </Show>
        )}
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteDocumentDialog(props: {
  document: DocumentDetail;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteDocument({
        data: { id: props.document.id, businessId: props.businessId },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete document");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Document</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.document.name}"</span> and its
            associated file. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View ----------------------------------------------------------------------

function DocumentView(props: {
  document: DocumentDetail;
  businessId: string;
  prefetchedUrl?: string | null;
  setMode: (m: Mode) => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [deleteOpen, setDeleteOpen] = createSignal(false);
  const [downloading, setDownloading] = createSignal(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Short badge labels for all workflow steps
  const STEP_BADGE_LABEL: Partial<Record<WorkflowStep, string>> = {
    ocr: "OCR",
    ai_extract: "AI",
    vendor_match: "Vendor",
    similar_txn: "Matching",
    create_txn: "Creating",
    link_txn: "Linking",
  };

  const workflowStepsQuery = createQuery(() => ({
    ...WorkflowStepsResource.options({
      businessId: props.businessId,
      entityType: "document",
      entityId: props.document.id,
    }),
    refetchInterval: (query) => {
      const hasNonTerminalStep = query.state.data?.some(
        (s) => s.status === "pending" || s.status === "running",
      );
      return hasNonTerminalStep ? 2000 : false;
    },
  }));

  const activeStepLabel = createMemo(() => {
    const running = workflowStepsQuery.data?.find(
      (s) => s.status === "running",
    );
    if (!running) return null;
    return (
      STEP_BADGE_LABEL[running.step as WorkflowStep] ??
      WORKFLOW_STEP_LABELS[running.step as WorkflowStep] ??
      null
    );
  });

  // When vendor_match transitions to complete, invalidate the associations
  // cache so the new document↔vendor link appears immediately.
  createEffect(
    on(
      () => workflowStepsQuery.data,
      (steps, prevSteps) => {
        if (!steps || !prevSteps) return;
        const vendorStep = steps.find((s) => s.step === "vendor_match");
        const prevVendorStep = prevSteps.find((s) => s.step === "vendor_match");
        if (
          vendorStep?.status === "complete" &&
          prevVendorStep?.status !== "complete"
        ) {
          qc.invalidateQueries({
            queryKey: [
              "association",
              props.businessId,
              "document",
              props.document.id,
            ],
          });
        }
      },
    ),
  );

  const typeLabel = () =>
    DocumentTypes.find((t) => t.value === props.document.type)?.label ??
    props.document.type ??
    null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  async function handleDownload() {
    if (!props.document.fileKey) {
      toast.error("No file attached to this document");
      return;
    }
    setDownloading(true);
    try {
      const { presignedUrl } = await getPresignedDownloadUrl({
        data: { id: props.document.id, businessId: props.businessId },
      });
      const anchor = document.createElement("a");
      anchor.href = presignedUrl;
      anchor.download = props.document.fileName ?? props.document.name;
      anchor.target = "_blank";
      anchor.rel = "noreferrer";
      anchor.click();
    } catch {
      toast.error("Failed to generate download link");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconFileText class="size-5" />}
        title={props.document.name}
        badge={typeLabel() ?? undefined}
        date={props.document.date ?? undefined}
        statusBadge={
          <ProcessingStatusBadge
            status={props.document.processingStatus}
            activeStepLabel={activeStepLabel()}
          />
        }
        menuContent={
          <>
            <Show when={props.document.fileKey}>
              <DropdownMenuItem
                onClick={() => void handleDownload()}
                disabled={downloading()}
              >
                <IconDownload class="size-4" />
                {downloading() ? "Downloading..." : "Download"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </Show>
            <DropdownMenuItem
              onClick={() => {
                navigate({
                  to: "/dashboard/journal-entries/$journalEntryId",
                  params: { journalEntryId: "new" },
                  search: {
                    prefillDescription: props.document.name ?? undefined,
                    prefillDate: props.document.date
                      ? new Date(props.document.date)
                          .toISOString()
                          .split("T")[0]
                      : undefined,
                    prefillAmount:
                      props.document.amount != null
                        ? props.document.amount
                        : undefined,
                    prefillDocumentId: props.document.id,
                  },
                });
              }}
            >
              <IconCircleDollarSign class="size-4" />
              Add Journal Entry
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      {/* Processing failure — clear, calm attention state */}
      <Show when={props.document.processingStatus === "failed"}>
        <div
          role="alert"
          class="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <IconTriangleAlert class="mt-0.5 size-4 shrink-0 text-destructive" />
          <div class="min-w-0">
            <p class="text-sm font-medium text-destructive">
              Processing failed
            </p>
            <p class="mt-0.5 text-sm text-muted-foreground">
              {props.document.processingError ||
                "This document could not be processed automatically. You can still review it and edit the extracted details manually."}
            </p>
          </div>
        </div>
      </Show>

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              {/* Extracted accounting facts — the "ledger" summary */}
              <StandardRouteDetailCard
                icon={<IconTextAlignStart class="size-5" />}
                title="Details"
                contentClass=""
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit document"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                {/* Amount is the single most important accounting fact — make it the hero */}
                <div class="pb-5">
                  <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Amount
                  </p>
                  <p class="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight">
                    {props.document.amount != null
                      ? formatCurrency(props.document.amount)
                      : "—"}
                  </p>
                </div>

                <div class="divide-y divide-border border-t">
                  <InfoRow label="Vendor" value={props.document.vendor} />
                  <InfoRow
                    label="Date"
                    value={formatDisplayCalendarDate(props.document.date)}
                  />
                  <InfoRow label="Type" value={typeLabel()} />
                </div>

                <Show when={props.document.description}>
                  <div class="pt-5">
                    <p class="mb-1.5 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      Description
                    </p>
                    <p class="text-sm leading-relaxed text-foreground">
                      {props.document.description}
                    </p>
                  </div>
                </Show>

                <Show when={props.document.tags?.length}>
                  <div class="pt-5">
                    <p class="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      Tags
                    </p>
                    <div class="flex flex-wrap gap-1.5">
                      {props.document.tags?.map((tag) => (
                        <Badge variant="outline" class="font-normal">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Show>
              </StandardRouteDetailCard>

              {/* File metadata — hidden on mobile */}
              <Show when={!isMobile()}>
                <StandardRouteDetailCard
                  icon={<IconFileText class="size-5" />}
                  title="File"
                  contentClass="divide-y divide-border"
                >
                  <InfoRow
                    label="Name"
                    value={
                      (props.document.fileName ?? props.document.name) || null
                    }
                  />
                  <InfoRow
                    label="Size"
                    value={
                      props.document.fileSizeBytes != null
                        ? formatBytes(props.document.fileSizeBytes)
                        : null
                    }
                  />
                  <InfoRow
                    label="Uploaded"
                    value={formatDisplayDate(props.document.createdAt) ?? null}
                  />
                </StandardRouteDetailCard>
              </Show>
            </div>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue={
                    isPdfDocument(props.document) ||
                    isImageDocument(props.document) ||
                    isSpreadsheetDocument(props.document) ||
                    isTextDocument(props.document)
                      ? "viewer"
                      : "metadata"
                  }
                  class="flex h-full min-h-0 flex-col"
                >
                  <TabsList class="w-full">
                    <Show
                      when={
                        isPdfDocument(props.document) ||
                        isImageDocument(props.document) ||
                        isSpreadsheetDocument(props.document) ||
                        isTextDocument(props.document)
                      }
                    >
                      <TabsTrigger value="viewer" class="flex-1">
                        <IconFileText class="mr-2 size-4" />
                        <span class="hidden sm:inline">Viewer</span>
                      </TabsTrigger>
                    </Show>
                    <TabsTrigger value="metadata" class="flex-1">
                      <IconInfo class="mr-2 size-4" />
                      <span class="hidden sm:inline">Metadata</span>
                    </TabsTrigger>
                    <TabsTrigger value="workflow" class="flex-1">
                      <IconLoaderCircle class="mr-2 size-4" />
                      <span class="hidden sm:inline">Workflow</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack class="min-w-0">
                    <Show
                      when={
                        isPdfDocument(props.document) ||
                        isImageDocument(props.document)
                      }
                    >
                      {" "}
                      <StandardRouteDetailTabsContent
                        value="viewer"
                        class="flex min-w-0 flex-col overflow-hidden"
                        forceMount
                      >
                        <DocumentPdfPreview
                          businessId={props.businessId}
                          document={props.document}
                          prefetchedUrl={props.prefetchedUrl}
                        />
                      </StandardRouteDetailTabsContent>
                    </Show>

                    <Show
                      when={
                        !isPdfDocument(props.document) &&
                        isSpreadsheetDocument(props.document)
                      }
                    >
                      <StandardRouteDetailTabsContent
                        value="viewer"
                        class="flex min-w-0 flex-col overflow-hidden"
                        forceMount
                      >
                        <DocumentSpreadsheetPreview
                          businessId={props.businessId}
                          document={props.document}
                        />
                      </StandardRouteDetailTabsContent>
                    </Show>

                    <Show
                      when={
                        !isPdfDocument(props.document) &&
                        !isSpreadsheetDocument(props.document) &&
                        isTextDocument(props.document)
                      }
                    >
                      <StandardRouteDetailTabsContent
                        value="viewer"
                        class="flex min-w-0 flex-col overflow-hidden"
                        forceMount
                      >
                        <DocumentTextPreview
                          businessId={props.businessId}
                          document={props.document}
                        />
                      </StandardRouteDetailTabsContent>
                    </Show>

                    <StandardRouteDetailTabsContent
                      value="metadata"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <Grid cols={1} colsMd={2} class="w-full gap-4">
                          <Col span={1} spanMd={2}>
                            <TextField>
                              <TextFieldInput
                                label="Original File Name"
                                value={
                                  props.document.fileName ?? props.document.name
                                }
                                disabled
                              />
                            </TextField>
                          </Col>
                          <Col>
                            <TextField>
                              <TextFieldInput
                                label="MIME Type"
                                value={props.document.fileMimeType ?? "N/A"}
                                disabled
                              />
                            </TextField>
                          </Col>
                          <Col>
                            <TextField>
                              <TextFieldInput
                                label="Size"
                                value={
                                  props.document.fileSizeBytes != null
                                    ? formatBytes(props.document.fileSizeBytes)
                                    : "N/A"
                                }
                                disabled
                              />
                            </TextField>
                          </Col>
                          <Col>
                            <TextField>
                              <TextFieldInput
                                label="Uploaded"
                                value={
                                  formatDisplayDate(props.document.createdAt) ??
                                  "N/A"
                                }
                                disabled
                              />
                            </TextField>
                          </Col>
                          <Col>
                            <TextField>
                              <TextFieldInput
                                label="Last Modified"
                                value={
                                  formatDisplayDate(props.document.updatedAt) ??
                                  "N/A"
                                }
                                disabled
                              />
                            </TextField>
                          </Col>
                        </Grid>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{ type: "document", id: props.document.id }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "vendor",
                              defaultVisibility: {
                                address: false,
                                website: false,
                                contactName: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="workflow"
                      class="overflow-auto"
                      forceMount
                    >
                      <WorkflowStepsPanel
                        entityType="document"
                        entityId={props.document.id}
                        businessId={props.businessId}
                        processingStatus={props.document.processingStatus}
                        docType={props.document.type}
                      />
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteDocumentDialog
        document={props.document}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

function DocumentPdfPreview(props: {
  document: DocumentDetail;
  businessId: string;
  prefetchedUrl?: string | null;
}) {
  const [previewUrl, setPreviewUrl] = createSignal<string | null>(
    props.prefetchedUrl ?? null,
  );
  const [loadError, setLoadError] = createSignal<string | null>(null);

  const loadPreview = async () => {
    if (!props.document.fileKey) {
      setPreviewUrl(null);
      setLoadError("No file attached to this document.");
      return;
    }

    setLoadError(null);

    try {
      const { presignedUrl } = await getPresignedDownloadUrl({
        data: { id: props.document.id, businessId: props.businessId },
      });
      setPreviewUrl(presignedUrl);
    } catch {
      setPreviewUrl(null);
      setLoadError("Failed to load the PDF preview.");
    }
  };

  // Stable memo — only changes when id or fileKey actually changes,
  // not when the query refetches with the same document (e.g. status updates).
  const previewKey = createMemo(
    () =>
      `${props.document.id}:${props.document.fileKey ?? ""}:${props.businessId}`,
  );

  createEffect(
    on(previewKey, () => {
      // Skip the initial run when the loader already provided a URL —
      // re-fetching would replace the URL and force a full PDF reload.
      if (previewUrl()) return;
      void loadPreview();
    }),
  );

  return (
    <div class="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <Show
        when={isImageDocument(props.document)}
        fallback={
          <PdfViewer
            src={previewUrl()}
            class="h-full min-h-0 flex-1"
            singlePageHeight
            statusMessage={
              loadError() ??
              (!previewUrl() ? "Loading PDF preview..." : undefined)
            }
          />
        }
      >
        <Show
          when={previewUrl()}
          fallback={
            <div class="flex min-h-96 items-center justify-center gap-2 text-sm text-muted-foreground">
              <IconLoaderCircle class="size-4 animate-spin" />
              {loadError() ?? "Loading preview..."}
            </div>
          }
        >
          <div class="flex min-h-96 flex-1 items-center justify-center overflow-auto bg-muted/30 p-6">
            <img
              src={previewUrl()!}
              alt={props.document.fileName ?? props.document.name}
              class="max-w-full max-h-full object-contain rounded-md shadow-sm ring-1 ring-border"
            />
          </div>
        </Show>
      </Show>
    </div>
  );
}

function isImageDocument(document: DocumentDetail) {
  const fileName = document.fileName ?? document.name;
  return (
    document.fileMimeType?.startsWith("image/") ||
    /\.(png|jpg|jpeg|gif|bmp|webp|tiff?|heic|heif|svg|avif)$/i.test(fileName)
  );
}

function isPdfDocument(document: DocumentDetail) {
  const fileName = document.fileName ?? document.name;
  return (
    document.fileMimeType === "application/pdf" ||
    fileName.toLowerCase().endsWith(".pdf")
  );
}

const SPREADSHEET_MIME_TYPES = new Set([
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
]);

function isSpreadsheetDocument(document: DocumentDetail) {
  const mime = (document.fileMimeType ?? "").toLowerCase();
  if (SPREADSHEET_MIME_TYPES.has(mime)) return true;

  const namesToCheck = [
    document.fileName ?? document.name,
    document.fileKey ?? "",
  ];
  return namesToCheck.some((n) => /\.(xlsx|xls|xlsm|xlsb)$/i.test(n));
}

const TEXT_MIME_TYPES = new Set([
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "text/xml",
  "application/json",
  "application/xml",
  "application/xhtml+xml",
]);

function isTextDocument(document: DocumentDetail) {
  if (
    document.fileMimeType &&
    (document.fileMimeType.startsWith("text/") ||
      TEXT_MIME_TYPES.has(document.fileMimeType))
  )
    return true;
  const namesToCheck = [
    document.fileName ?? document.name,
    document.fileKey ?? "",
  ];
  return namesToCheck.some((n) =>
    /\.(txt|md|csv|json|xml|html|htm|yaml|yml|toml|ini|log|ts|tsx|js|jsx|css|sh|env)$/i.test(
      n,
    ),
  );
}

function isMarkdownDocument(document: DocumentDetail) {
  if (document.fileMimeType === "text/markdown") return true;
  const namesToCheck = [
    document.fileName ?? document.name,
    document.fileKey ?? "",
  ];
  return namesToCheck.some((n) => /\.mdx?$/i.test(n));
}

/** Maximum spreadsheet file size (10 MB) for in-browser preview.
 *  Larger files are still downloadable but won't be parsed/rendered
 *  in the browser to keep the page responsive. */
const MAX_SPREADSHEET_PREVIEW_BYTES = 10 * 1024 * 1024;

function DocumentSpreadsheetPreview(props: {
  document: DocumentDetail;
  businessId: string;
}) {
  const [sheetData, setSheetData] = createSignal<any[] | null>(null);
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [isOversized, setIsOversized] = createSignal(false);

  const fileSizeExceedsLimit = () =>
    props.document.fileSizeBytes != null &&
    props.document.fileSizeBytes > MAX_SPREADSHEET_PREVIEW_BYTES;

  const loadSpreadsheet = async () => {
    if (!props.document.fileKey) {
      setSheetData(null);
      setLoadError("No file attached to this document.");
      return;
    }

    if (fileSizeExceedsLimit()) {
      setIsOversized(true);
      return;
    }

    setIsOversized(false);
    setLoadError(null);

    try {
      const { presignedUrl } = await getPresignedDownloadUrl({
        data: { id: props.document.id, businessId: props.businessId },
      });

      const response = await fetch(presignedUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const fileName = props.document.fileName ?? props.document.name;
      const file = new File([blob], fileName, {
        type: blob.type || props.document.fileMimeType || undefined,
      });

      const parsed = await importFromExcel(file);
      setSheetData(
        Array.isArray(parsed) && parsed.length ? parsed : [{ name: "Sheet1" }],
      );
    } catch {
      setSheetData(null);
      setLoadError("Failed to load the spreadsheet preview.");
    }
  };

  createEffect(
    on(
      () => [props.document.id, props.document.fileKey, props.businessId],
      () => {
        void loadSpreadsheet();
      },
    ),
  );

  return (
    <div class="flex h-full flex-1 flex-col overflow-hidden">
      <Show when={isOversized()}>
        <div class="flex min-h-130 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
          <IconFileText class="size-10 text-muted-foreground/60" />
          <p class="max-w-xs text-center">
            This file is too large to preview in the browser. Download it to
            view the contents.
          </p>
        </div>
      </Show>
      <Show
        when={!isOversized() && sheetData()}
        fallback={
          !isOversized() && (
            <div class="flex min-h-130 items-center justify-center text-sm text-muted-foreground">
              {loadError() ?? "Loading spreadsheet preview..."}
            </div>
          )
        }
      >
        {(data) => (
          <Suspense
            fallback={
              <div class="flex min-h-130 items-center justify-center text-sm text-muted-foreground">
                Loading spreadsheet preview...
              </div>
            }
          >
            <Spreadsheet
              initialData={data()}
              onChange={() => undefined}
              mode="view"
              class="h-full min-h-130 flex-1"
            />
          </Suspense>
        )}
      </Show>
    </div>
  );
}

// -- Text Preview --------------------------------------------------------------

function DocumentTextPreview(props: {
  document: DocumentDetail;
  businessId: string;
}) {
  const [content, setContent] = createSignal<string | null>(null);
  const [loadError, setLoadError] = createSignal<string | null>(null);

  const loadText = async () => {
    if (!props.document.fileKey) {
      setContent(null);
      setLoadError("No file attached to this document.");
      return;
    }
    setLoadError(null);
    try {
      const { presignedUrl } = await getPresignedDownloadUrl({
        data: { id: props.document.id, businessId: props.businessId },
      });
      const res = await fetch(presignedUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setContent(await res.text());
    } catch {
      setContent(null);
      setLoadError("Failed to load the file.");
    }
  };

  createEffect(
    on(
      () => [props.document.id, props.document.fileKey, props.businessId],
      () => {
        void loadText();
      },
    ),
  );

  return (
    <div class="flex h-full flex-1 flex-col overflow-hidden">
      <Show
        when={content()}
        fallback={
          <div class="flex min-h-96 items-center justify-center text-sm text-muted-foreground">
            {loadError() ?? "Loading..."}
          </div>
        }
      >
        {(text) => (
          <Show
            when={isMarkdownDocument(props.document)}
            fallback={
              <pre class="h-full flex-1 min-h-0 overflow-auto rounded-md bg-muted/30 p-4 text-sm leading-relaxed text-foreground whitespace-pre-wrap overflow-wrap-anywhere">
                {text()}
              </pre>
            }
          >
            <div class="h-full flex-1 min-h-0 overflow-auto rounded-md bg-muted/30 p-4">
              <div class="prose prose-sm dark:prose-invert max-w-none">
                <Markdown content={text()} />
              </div>
            </div>
          </Show>
        )}
      </Show>
    </div>
  );
}

// -- Edit ----------------------------------------------------------------------

function DocumentEdit(props: {
  document: DocumentDetail;
  businessId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [serverError, setServerError] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const qc = useQueryClient();

  const vendorsQuery = createQuery(() => ({
    ...VendorsResource.options({
      businessId: props.businessId,
      limit: 200,
    }),
    placeholderData: keepPreviousData,
  }));

  const form = useDocumentForm(props.businessId, props.document, {
    onSuccess: () => {
      toast.success("Document updated successfully");
      props.onSaved();
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  /**
   * Freeform vendor names typed into the combo box are created as vendors
   * on save (name is the only required field). Case-insensitive check so
   * "Acme Corp" vs "acme corp" doesn't create a duplicate.
   */
  async function ensureVendorExists(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const exists = (vendorsQuery.data?.items ?? []).some(
      (v) => v.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) return;
    await createVendor({
      data: { businessId: props.businessId, item: { name: trimmed } },
    });
    qc.invalidateQueries({ queryKey: ["vendor", props.businessId] });
  }

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await ensureVendorExists(form.getFieldValue("vendor") ?? "");
      await form.handleSubmit();
    } catch {
      toast.error("Failed to create vendor");
      setServerError("Failed to create vendor");
    } finally {
      setSaving(false);
    }
  }

  return (
    <StandardRouteDetailEditCard
      title="Edit document"
      description="Correct the extracted details. Changes apply immediately on save."
      formId="document-edit-form"
      onCancel={props.onCancel}
      saving={saving()}
    >
      <form id="document-edit-form" onSubmit={handleSubmit}>
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          <Col span={1} spanLg={2}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Document name"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="type">
              {(field) => (
                <FormField label="Type" field={field()}>
                  <MultiSelect
                    placeholder="Select type..."
                    searchPlaceholder="Search types..."
                    options={DocumentTypes}
                    value={
                      field().state.value ? [field().state.value as string] : []
                    }
                    onChange={(vals) =>
                      field().handleChange(
                        (vals[0] as DocumentType) ?? undefined,
                      )
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="date">
              {(field) => (
                <DatePickerField
                  label="Date"
                  value={field().state.value ?? ""}
                  onChange={field().handleChange}
                  onBlur={field().handleBlur}
                  error={field().state.meta.errors[0]?.toString()}
                />
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="amount">
              {(field) => (
                <FormField label="Amount" field={field()}>
                  <TextFieldInput
                    type="text"
                    inputmode="decimal"
                    placeholder="0.00"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="vendor">
              {(field) => (
                <FormField label="Vendor" field={field()}>
                  <VendorComboBox
                    businessId={props.businessId}
                    value={field().state.value ?? ""}
                    onChange={(name) => field().handleChange(name)}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="tags">
              {(field) => (
                <FormField label="Tags" field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Comma-separated tags"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="description">
              {(field) => (
                <FormField label="Description" field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Optional description"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

// -- Vendor combo box ----------------------------------------------------------

/**
 * Vendor picker for the document edit form. A searchable combo box listing
 * the business's existing vendors with freeform entry — a typed name that
 * doesn't match an existing vendor is created automatically on save.
 */
function VendorComboBox(props: {
  businessId: string;
  value: string;
  onChange: (name: string) => void;
}) {
  const vendorsQuery = createQuery(() => ({
    ...VendorsResource.options({
      businessId: props.businessId,
      limit: 200,
    }),
    placeholderData: keepPreviousData,
  }));

  const vendorOptions = createMemo(() =>
    (vendorsQuery.data?.items ?? []).map((v) => ({
      label: v.name,
      value: v.name,
    })),
  );

  return (
    <MultiSelect
      placeholder="Select or type a vendor..."
      searchPlaceholder="Select or type a vendor..."
      options={vendorOptions()}
      value={props.value ? [props.value] : []}
      onChange={(vals) => props.onChange(vals[0] ?? "")}
      multiple={false}
      allowFreeformText
    />
  );
}

// -- Helpers -------------------------------------------------------------------

function InfoRow(props: { label: string; value: string | null | undefined }) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "-"}</h6>
    </div>
  );
}

type FieldApi = {
  state: { meta: { errors: (string | { message: string } | undefined)[] } };
};

function FormField(props: {
  label: string;
  required?: boolean;
  field: FieldApi;
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}
